/**
 * @fileoverview Mastra API client
 * @description Lightweight fetch helpers for the admin dashboard to call the Mastra backend
 */

import { supabase } from '@/lib/supabase';
import { getMastraErrorMessage, getMastraNetworkErrorMessage } from '@/lib/mastra-errors';

// Empty string = same-origin (Vite proxy / Coolify preview). Unset falls back to local Mastra.
const configuredMastraUrl = import.meta.env.VITE_MASTRA_URL as string | undefined;
const MASTRA_URL =
  configuredMastraUrl !== undefined
    ? configuredMastraUrl
    : ((import.meta.env.EXPO_PUBLIC_MASTRA_URL as string) || 'http://localhost:4112');

/** A non-2xx Mastra response. `body` is the parsed JSON body, or null. */
export class MastraRequestError extends Error {
  readonly body: unknown;

  constructor(message: string, body: unknown) {
    super(message);
    this.name = 'MastraRequestError';
    this.body = body;
  }
}

export async function postMastra<T>(
  path: string,
  body: unknown,
  options?: { accessToken?: string | null; signal?: AbortSignal },
): Promise<T> {
  const baseUrl = MASTRA_URL.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  // Prefer explicit token, but fall back to the current Supabase session to
  // avoid race conditions on first load after sign-in.
  const token =
    options?.accessToken ??
    (await supabase.auth.getSession().then((r) => r.data.session?.access_token ?? null));
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    // Fallback for dev/proxy setups that strip the Authorization header.
    headers['X-Supabase-Token'] = token;
  }

  let res: Response;
  const startedAt = performance.now();
  try {
    res = await fetch(`${baseUrl}${normalizedPath}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: options?.signal,
    });
  } catch (error) {
    throw new Error(getMastraNetworkErrorMessage(error, performance.now() - startedAt));
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let body: unknown = null;
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON error bodies keep body null.
    }
    throw new MastraRequestError(getMastraErrorMessage(text, res.status), body);
  }

  return (await res.json()) as T;
}
